import {
  computed,
  defineComponent,
  inject,
  nextTick,
  onMounted,
  ref,
  unref,
  watch,
} from 'vue'
import { isArray, isFunction, isNumber } from '@jdesign-vue/utils'
import { tableV2RowProps } from '../row'
import { TableV2InjectionKey } from '../tokens'
import { placeholderSign } from '../private'

import type { CSSProperties, RendererElement, RendererNode, VNode } from 'vue'
import type { RowEventHandlers, TableV2RowProps } from '../row'

type CustomizedCellsType = VNode<
  RendererNode,
  RendererElement,
  {
    [key: string]: any
  }
>[]

type DefaultCellsType = VNode<
  RendererNode,
  RendererElement,
  {
    [key: string]: any
  }
>[][]

type ColumnCellsType = DefaultCellsType | CustomizedCellsType

const useTableRow = (props: TableV2RowProps) => {
  const {
    isScrolling: injectedIsScrolling,
    hoveringRowKey,
    ns: tableNs,
  } = inject(TableV2InjectionKey)!

  const measured = ref(false)
  const rowRef = ref<HTMLElement>()
  const measurable = computed(() => {
    return isNumber(props.estimatedRowHeight) && props.rowIndex >= 0
  })

  // 当行被复用时（rowKey变化），重置measured状态并触发重新测量
  watch(
    () => props.rowKey,
    () => {
      measured.value = false
      // 在nextTick中重新测量
      nextTick(() => {
        if (unref(measurable)) {
          doMeasure(false)
        }
      })
    }
  )

  const doMeasure = (isInit = false) => {
    const $rowRef = unref(rowRef)
    if (!$rowRef) return
    const { columns, onRowHeightChange, rowKey, rowIndex, style } = props
    const { height } = $rowRef.getBoundingClientRect()
    measured.value = true

    nextTick(() => {
      if (isInit || height !== Number.parseInt(style!.height as string)) {
        const firstColumn = columns[0]
        const isPlaceholder = firstColumn?.placeholderSign === placeholderSign
        onRowHeightChange?.(
          { rowKey, height, rowIndex },
          firstColumn && !isPlaceholder && firstColumn.fixed
        )
      }
    })
  }

  // 关键性能优化：handler 必须保持引用稳定，否则虚拟列表里同一个 row 组件被复用、
  // rowIndex 等 props 变化时，computed 会返回新的箭头闭包，spread 到 div 上后
  // Vue 会对每个事件 removeEventListener + addEventListener。在固定列表格 + 2000+
  // 数据场景下，每秒可触发上千次 listener 切换（Listeners 直接涨到 1 万+），
  // 这是滚动 CPU 占用居高不下的主要原因。
  //
  // 这里用一个 stableHandlers 字典缓存，handler 内部通过闭包读取 `props.xxx`
  // 拿到最新值（props 本身是 reactive proxy，组件 setup 期间引用恒定），从而
  // 在 row 复用时既能拿到正确的 rowData/rowIndex，又不会触发 Vue patch 重挂监听。
  type EventHandler = (e: Event) => void
  const stableHandlers: Record<string, EventHandler> = {}

  const eventHandlers = computed(() => {
    const handlers = props.rowEventHandlers || ({} as RowEventHandlers)
    const result = {} as Record<keyof RowEventHandlers, EventHandler>

    Object.keys(handlers).forEach((eventName) => {
      const key = eventName as keyof RowEventHandlers
      if (!isFunction(handlers[key])) return
      if (!stableHandlers[eventName]) {
        stableHandlers[eventName] = (event: Event) => {
          const latest = props.rowEventHandlers?.[key]
          if (isFunction(latest)) {
            latest({
              event: event as any,
              rowData: props.rowData,
              rowIndex: props.rowIndex,
              rowKey: props.rowKey,
            })
          }
        }
      }
      result[key] = stableHandlers[eventName]
    })

    if (props.onRowHover) {
      ;[
        { name: 'onMouseleave' as const, hovered: false },
        { name: 'onMouseenter' as const, hovered: true },
      ].forEach(({ name, hovered }) => {
        if (!stableHandlers[name]) {
          stableHandlers[name] = (event: Event) => {
            if (unref(injectedIsScrolling)) return
            props.onRowHover?.({
              event: event as MouseEvent,
              hovered,
              rowData: props.rowData,
              rowIndex: props.rowIndex,
              rowKey: props.rowKey,
            })
            const userHandler = props.rowEventHandlers?.[name]
            if (isFunction(userHandler)) {
              userHandler({
                event: event as MouseEvent,
                rowData: props.rowData,
                rowIndex: props.rowIndex,
                rowKey: props.rowKey,
              })
            }
          }
        }
        result[name] = stableHandlers[name]
      })
    }
    return result
  })

  const onExpand = (expanded: boolean) => {
    const { onRowExpand, rowData, rowIndex, rowKey } = props

    onRowExpand?.({
      expanded,
      rowData,
      rowIndex,
      rowKey,
    })
  }

  onMounted(() => {
    if (unref(measurable)) {
      doMeasure(true)
    }
  })

  return {
    hoveringRowKey,
    injectedIsScrolling,
    measurable,
    measured,
    rowRef,
    tableNs,
    eventHandlers,
    onExpand,
  }
}

const COMPONENT_NAME = 'JdTableV2TableRow'

const TableV2Row = defineComponent({
  name: COMPONENT_NAME,
  props: tableV2RowProps,
  setup(props, { expose, slots, attrs }) {
    const {
      eventHandlers,
      hoveringRowKey,
      injectedIsScrolling,
      measurable,
      measured,
      rowRef,
      tableNs,
      onExpand,
    } = useTableRow(props)

    expose({
      /**
       * @description manually dispatching expand action on row.
       */
      onExpand,
    })

    // 关键性能优化：缓存上一次的 ColumnCells vnode 树。
    //
    // 虚拟列表滚动跨越 1 行时，30 个 visible row 中 29 个共享行（rowData/rowIndex/style 都
    // 没变）实际上 *不需要* 重新生成 cells，但 build-grid 重渲染会驱动每个 row instance 的
    // render 函数重跑。每次 render 重跑都会 columns.map() 生成 10+ 个新 cell vnode →
    // Vue patch 逐个 diff，新旧 props 内容相同但引用全变 → patchProps 比对成本仍然存在。
    //
    // 这里在 row render 入口处做"等价性检查"：rowData、rowIndex、style、columns、columnsStyles
    // 引用都没变时，直接复用上一次的 vnode 数组。Vue diff 看到 children 引用相等会直接跳过。
    let memoRowData: any
    let memoRowIndex: number | undefined
    let memoStyle: CSSProperties | undefined
    let memoColumns: TableV2RowProps['columns'] | undefined
    let memoColumnsStyles: TableV2RowProps['columnsStyles'] | undefined
    let memoCellIsScrolling: boolean | undefined
    let memoDepth: number | undefined
    let memoCells: ColumnCellsType | undefined

    return () => {
      const {
        columns,
        columnsStyles,
        expandColumnKey,
        depth,
        rowData,
        rowIndex,
        style,
        isScrolling: rowIsScrolling,
      } = props

      // grid slot 仅在 useIsScrolling=true 时透传 props.isScrolling，否则用 table-v2 自身的滚动状态兜底
      const cellIsScrolling =
        typeof rowIsScrolling === 'boolean'
          ? rowIsScrolling
          : unref(injectedIsScrolling)

      // 固定列三表 hover 同步：在 Row 内按 inject 计算 class，避免 hoveringRowKey 变化时
      // 整棵 TableV2 + tableSlots 重建（仅本行与上一 hover 行会 patch）
      const baseClass = props.class
      const rowClass = [
        ...(Array.isArray(baseClass)
          ? baseClass
          : baseClass != null
          ? [baseClass]
          : []),
        !cellIsScrolling &&
          unref(hoveringRowKey) === props.rowKey &&
          tableNs.is('hovered'),
      ].filter(Boolean)

      let ColumnCells: ColumnCellsType
      if (
        memoCells &&
        memoRowData === rowData &&
        memoRowIndex === rowIndex &&
        memoStyle === style &&
        memoColumns === columns &&
        memoColumnsStyles === columnsStyles &&
        memoCellIsScrolling === cellIsScrolling &&
        memoDepth === depth
      ) {
        ColumnCells = memoCells
      } else {
        ColumnCells = columns.map((column, columnIndex) => {
          const expandable =
            isArray(rowData.children) &&
            rowData.children.length > 0 &&
            column.key === expandColumnKey

          return slots.cell!({
            column,
            columns,
            columnIndex,
            depth,
            style: columnsStyles[column.key],
            rowData,
            rowIndex,
            isScrolling: cellIsScrolling,
            expandIconProps: expandable
              ? {
                  rowData,
                  rowIndex,
                  onExpand,
                }
              : undefined,
          })
        })
        memoCells = ColumnCells
        memoRowData = rowData
        memoRowIndex = rowIndex
        memoStyle = style
        memoColumns = columns
        memoColumnsStyles = columnsStyles
        memoCellIsScrolling = cellIsScrolling
        memoDepth = depth
      }

      if (slots.row) {
        ColumnCells = slots.row({
          cells: ColumnCells.map((node) => {
            if (isArray(node) && node.length === 1) {
              return node[0]
            }
            return node
          }),
          style,
          columns,
          depth,
          rowData,
          rowIndex,
          isScrolling: cellIsScrolling,
        })
      }

      if (unref(measurable)) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { height, ...exceptHeightStyle } = style || {}
        const _measured = unref(measured)
        // 当measured为false时，使用estimatedRowHeight作为初始高度，避免内容塌陷
        const estimatedStyle = _measured 
          ? style 
          : { ...exceptHeightStyle, height: `${props.estimatedRowHeight}px` }
        return (
          <div
            ref={rowRef}
            class={rowClass}
            style={estimatedStyle}
            role="row"
            {...attrs}
            {...unref(eventHandlers)}
          >
            {ColumnCells}
          </div>
        )
      }

      return (
        <div
          {...attrs}
          ref={rowRef}
          class={rowClass}
          style={style}
          role="row"
          {...unref(eventHandlers)}
        >
          {ColumnCells}
        </div>
      )
    }
  },
})

export default TableV2Row

export type TableV2RowCellRenderParam = {
  column: TableV2RowProps['columns'][number]
  columns: TableV2RowProps['columns']
  columnIndex: number
  depth: number
  style: CSSProperties
  rowData: any
  rowIndex: number
  isScrolling: boolean
  expandIconProps?: {
    rowData: any
    rowIndex: number
    onExpand: (expand: boolean) => void
  }
}
